import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  Grid,
  Button,
  IconButton,
  Typography,
  useTheme,
  Divider,
  Chip,
} from "@mui/material";

import AddEditButton from "../../reusable-components/AddEditButton/AddEditButton";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
import IconsSelect from "../../reusable-components/IconsSelect/IconsSelect";
import AutocompleteDropdown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";

import { FieldsContainer } from "./styles";
import { Props, State } from "./interfaces";
import { EmployeeGroupsApiService } from "../../services/employee-groups/employee-groups-api.service";
import { CompanyLegalEntitiesApiService } from "../../services/company-legal-entities/company-legal-entities-api.service";

import { toastifyResponse } from "../../utils/toastify-response/toastify-response";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import isEqual from "lodash/isEqual";
import { TnALocationCodesApiService } from "../../services/tna-location-codes/tna-location-codes-api.service";
import { TnALocationCodeDTO } from "../../models/tna-location-codes/tna-location-code-dto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import AddAllowedUser from "./AddAllowedUser";
import { UserDTO, UserRef } from "../../models/user";

export const AddEditTnALocationCodes = ({
  effect = "add",
  asLink = false,
  linkText,
  item,
  handleNotifyParent,
  buttonVariant,
  buttonText,
  customButtonStyle,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const tRP = "components.tna_location_codes";
  const tRPFields = `${tRP}.fields`;

  //#region default item
  const DEFAULT_ITEM: Partial<TnALocationCodeDTO> = useMemo(() => {
    return {
      name: item?.name ?? "",
      code: item?.code ?? "",
      shortCode: item?.shortCode ?? "",
      icon: item?.icon ?? "",
      employeeGroups: item?.employeeGroups ?? [],
      legalEntities: item?.legalEntities ?? [],
      keyForQRCode: item?.keyForQRCode ?? "",
      usersAllowedToGenerateQRCode: [],
    };
  }, [item]);
  //#ednregion

  //#region initial state
  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      error: "",
      item: item ?? (DEFAULT_ITEM as TnALocationCodeDTO),
      isCCDOpen: false,
      areFieldsValidated: effect === "edit",
    };
  }, [effect, DEFAULT_ITEM, item]);
  //#endregion

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  //#region change field
  const handleChangeField = useCallback(
    (field: keyof TnALocationCodeDTO, value: TnALocationCodeDTO[keyof TnALocationCodeDTO]) => {
      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          [field]: value,
        },
      }));
    },
    []
  );
  //#endregion

  //#region save
  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const response = await new TnALocationCodesApiService().saveItems([
        {
          ...state.item,
          ...(effect === "add" && { id: uuidv4() }),
        },
      ]);

      if (!response.error) {
        effect === "add" && setState({ ...INITIAL_STATE });
        effect === "edit" &&
          setState((state) => ({
            ...state,
            isDialogOpen: false,
            isCCDOpen: false,
          }));

        handleNotifyParent && handleNotifyParent();
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      }

      toastifyResponse(response);
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  }, [INITIAL_STATE, effect, handleNotifyParent, state.item]);
  //#endregion

  //#region discard , reset
  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.item, INITIAL_STATE.item)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };
  //#endregion

  //#region validate fields
  const areFieldsValidated = useCallback((): boolean => {
    return !!(state.item.name && state.item.code && state.item.shortCode && state.item.keyForQRCode);
  }, [state.item]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.item, areFieldsValidated]);
  //#endregion

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        item: item,
      }));

    return () => {
      if (effect === "add") setState({ ...INITIAL_STATE });
    };
  }, [item, effect, INITIAL_STATE]);

  //#region remove allowed user
  const removeUser = useCallback((userId: string) => {
    setState((state) => ({
      ...state,
      item: {
        ...state.item,
        usersAllowedToGenerateQRCode: [...state.item.usersAllowedToGenerateQRCode.filter((user) => user.id !== userId)],
      },
    }));
  }, []);
  //#endregion

  return (
    <>
      <AddEditButton
        title={t(`buttons.${effect}`)}
        effect={effect}
        asLink={asLink}
        linkText={linkText}
        onClick={() => {
          setState((state) => ({ ...state, isDialogOpen: true }));
        }}
        {...(buttonVariant === "text" && {
          buttonText: !!buttonText ? buttonText : effect === "edit" ? "Edit" : "Add",
        })}
        customStyle={customButtonStyle}
      />
      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <DialogTitle
            title={effect === "add" ? t(`${tRP}.add_title`) : t(`${tRP}.edit_title`)}
            handleClose={resetAndCloseDialog}
          />
          <DialogContent>
            <FieldsContainer>
              <Box>
                <TextField
                  required
                  label={t(`common_words.name`)}
                  value={state.item.name}
                  onChange={({ target: { value } }) => handleChangeField("name", value)}
                />
                <TextField
                  required
                  label={t(`common_words.code`)}
                  value={state.item.code}
                  onChange={({ target: { value } }) => handleChangeField("code", value)}
                />
              </Box>
              <Box>
                <TextField
                  required
                  label={t(`${tRPFields}.short_code`)}
                  value={state.item.shortCode}
                  onChange={({ target: { value } }) => handleChangeField("shortCode", value)}
                />
                <TextField
                  required
                  label={t(`${tRPFields}.qr_code_key`)}
                  value={state.item.keyForQRCode}
                  onChange={({ target: { value } }) => handleChangeField("keyForQRCode", value)}
                />
              </Box>

              <Grid
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <Grid
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <Typography>Icon: </Typography>
                  <IconsSelect
                    selectedIcon={state.item.icon}
                    handleNotifyParent={(iconContent: string) => handleChangeField("icon", iconContent)}
                    isLocationIcons={true}
                  />
                </Grid>
                <Box>
                  <AutocompleteDropdown
                    labelKey={t(`${tRPFields}.employee_groups`)}
                    propsItem={state.item}
                    field="employeeGroups"
                    handleChange={handleChangeField}
                    serviceApi={new EmployeeGroupsApiService()}
                    isMultiSelect={true}
                    selectedValue={state.item.employeeGroups || []}
                  />

                  <AutocompleteDropdown
                    labelKey={t(`${tRPFields}.legal_entities`)}
                    propsItem={state.item}
                    field="legalEntities"
                    handleChange={handleChangeField}
                    serviceApi={new CompanyLegalEntitiesApiService()}
                    isMultiSelect={true}
                    selectedValue={state.item.legalEntities || []}
                  />
                </Box>
              </Grid>
              <Divider
                orientation="horizontal"
                flexItem
              />
              <Grid>
                <Grid style={{ display: "flex", alignItems: "center" }}>
                  <Typography>
                    <b>{t(`${tRPFields}.users_allowed_to_generate_qr_code`)} : </b>
                  </Typography>
                  <AddAllowedUser
                    handleAllowUser={(user: UserDTO) =>
                      handleChangeField("usersAllowedToGenerateQRCode", [
                        ...state.item.usersAllowedToGenerateQRCode,
                        user as UserRef,
                      ])
                    }
                  />
                </Grid>
                <Grid>
                  {state.item.usersAllowedToGenerateQRCode.length > 0 ? (
                    state.item.usersAllowedToGenerateQRCode.map((user) => (
                      <Grid style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <Typography>{user.name}</Typography>
                        <IconButton onClick={() => removeUser(user.id)}>
                          <FontAwesomeIcon
                            icon={faTrash}
                            color={theme.palette.error.main}
                          />
                        </IconButton>
                      </Grid>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color={theme.palette.secondary.light}
                    >
                      {" "}
                      No allowed users yet
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </FieldsContainer>
          </DialogContent>
          <DialogActions
            handleSave={handleSave}
            disabledSaveButton={!areFieldsValidated()}
            handleCancel={resetAndCloseDialog}
          />
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};
